// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// System: service version and environment, auth, providers, database, queue, and workers.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CircleAlert, CircleCheck, CircleMinus, Cpu } from 'lucide-react'
import type { ReactNode } from 'react'
import { systemInfoQuery } from '@/api/queries/system'
import type { Provider, SystemInfo, WorkerStatus } from '@/api/types'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { KeyValueList } from '@/components/KeyValueList'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { Figure } from '@/features/ops/StatFigures'
import { cn } from '@/lib/cn'
import { formatDateTime, formatDuration, formatNumber } from '@/lib/format'

export const Route = createFileRoute('/system')({
  staticData: { crumb: 'System' },
  component: SystemPage,
})

const PROVIDERS: { key: Provider; label: string }[] = [
  { key: 'ses', label: 'Amazon SES' },
  { key: 'smtp', label: 'SMTP' },
  { key: 'log', label: 'Log' },
]

function SystemPage() {
  const info = useQuery(systemInfoQuery)
  const s = info.data
  return (
    <>
      <PageHeader
        title="System"
        description="Service version, authentication, providers, workers, and queue health. Refreshes every 15 seconds."
        meta={
          s && (
            <>
              <Badge tone={s.env === 'production' ? 'brass' : 'info'}>{s.env}</Badge>
              <span>
                Version <span className="font-mono text-ink">{s.version}</span>
              </span>
              <span>
                Up {formatDuration(s.uptime_seconds)}, since {formatDateTime(s.started_at)}
              </span>
            </>
          )
        }
      />
      {info.isError ? (
        <Card>
          <ErrorState error={info.error} onRetry={() => void info.refetch()} />
        </Card>
      ) : (
        <div className="space-y-5">
          <QueueStrip info={s} />
          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-5 xl:grid-cols-2">
            <Card>
              <CardHeader title="Service" />
              <CardBody>
                {!s ? (
                  <SkeletonText lines={6} />
                ) : (
                  <KeyValueList
                    items={[
                      { label: 'Version', value: <span className="font-mono">{s.version}</span> },
                      {
                        label: 'Revision',
                        value: s.revision ? (
                          <span className="font-mono">{s.revision.slice(0, 12)}</span>
                        ) : (
                          <span className="text-ink-3">Not recorded</span>
                        ),
                      },
                      { label: 'Environment', value: <span className="font-mono">{s.env}</span> },
                      {
                        label: 'Role',
                        value: (
                          <>
                            <span className="font-mono">{s.role}</span>{' '}
                            <span className="text-ink-3">
                              {s.role === 'all' ? '(API and worker in one process)' : ''}
                            </span>
                          </>
                        ),
                      },
                      {
                        label: 'Public URL',
                        value: s.public_base_url && (
                          <span className="font-mono">{s.public_base_url}</span>
                        ),
                      },
                      { label: 'Started', value: formatDateTime(s.started_at) },
                    ]}
                  />
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Authentication" />
              <CardBody>
                {!s ? (
                  <SkeletonText lines={4} />
                ) : (
                  <KeyValueList
                    items={[
                      {
                        label: 'Admin sign-in',
                        value:
                          s.auth_mode === 'dev' ? (
                            <span className="inline-flex items-center gap-1.5 text-warning">
                              <CircleAlert className="size-4" /> Development identities (no sign-in)
                            </span>
                          ) : (
                            'Microsoft Entra ID'
                          ),
                      },
                      {
                        label: 'Tenant',
                        value: s.entra && <span className="font-mono">{s.entra.tenant_id}</span>,
                        hideEmpty: true,
                      },
                      {
                        label: 'Audiences',
                        value: s.entra && (
                          <ul className="space-y-0.5 font-mono text-xs">
                            {s.entra.audiences.map((a) => (
                              <li key={a}>{a}</li>
                            ))}
                          </ul>
                        ),
                        hideEmpty: true,
                      },
                    ]}
                  />
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader
                title="Delivery providers"
                description="Which providers this deployment can use."
              />
              <CardBody>
                {!s ? (
                  <SkeletonText lines={4} />
                ) : (
                  <ul className="divide-y divide-line">
                    {PROVIDERS.map((p) => {
                      const v = s.providers[p.key]
                      return (
                        <li key={p.key} className="flex items-center gap-3 py-2 text-sm">
                          <Flag ok={v.configured} />
                          <span className="w-28 shrink-0 font-medium text-ink">{p.label}</span>
                          <span className="min-w-0 flex-1 truncate text-ink-2">
                            {v.configured ? v.detail || 'Configured' : v.detail || 'Not configured'}
                          </span>
                          {s.default_provider === p.key && <Badge tone="brass">Default</Badge>}
                        </li>
                      )
                    })}
                    <li className="flex items-center gap-3 py-2 text-sm">
                      <Flag ok={s.sandbox_sink !== 'none'} neutral={s.sandbox_sink === 'none'} />
                      <span className="w-28 shrink-0 font-medium text-ink">Sandbox sink</span>
                      <span className="min-w-0 flex-1 text-ink-2">
                        {s.sandbox_sink === 'smtp'
                          ? 'Captured test messages also get a copy in the SMTP sink.'
                          : 'Captured test messages are kept in the log only.'}
                      </span>
                    </li>
                  </ul>
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Database" />
              <CardBody>
                {!s ? (
                  <SkeletonText lines={3} />
                ) : (
                  <KeyValueList
                    items={[
                      {
                        label: 'Status',
                        value: (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5',
                              s.database.ok ? 'text-success' : 'text-danger',
                            )}
                          >
                            {s.database.ok ? (
                              <CircleCheck className="size-4" />
                            ) : (
                              <CircleAlert className="size-4" />
                            )}
                            {s.database.ok ? 'Connected' : 'Unreachable'}
                          </span>
                        ),
                      },
                      {
                        label: 'Round trip',
                        value:
                          s.database.latency_ms != null && `${s.database.latency_ms.toFixed(1)} ms`,
                      },
                      {
                        label: 'Schema version',
                        value: s.database.migration_version != null && (
                          <span className="font-mono">{s.database.migration_version}</span>
                        ),
                      },
                    ]}
                  />
                )}
              </CardBody>
            </Card>
          </div>
          <Workers info={s} isLoading={info.isPending} />
        </div>
      )}
    </>
  )
}

function Flag({ ok, neutral }: { ok: boolean; neutral?: boolean }) {
  if (neutral) return <CircleMinus className="size-4 shrink-0 text-ink-3" aria-label="Off" />
  return ok ? (
    <CircleCheck className="size-4 shrink-0 text-success" aria-label="Configured" />
  ) : (
    <CircleMinus className="size-4 shrink-0 text-ink-3" aria-label="Not configured" />
  )
}

function QueueStrip({ info }: { info: SystemInfo | undefined }) {
  const q = info?.queue
  const age = q?.oldest_ready_age_seconds
  return (
    <Card className="flex flex-wrap gap-px overflow-hidden bg-line">
      <Figure
        label="Ready"
        value={q ? formatNumber(q.ready) : undefined}
        note="Jobs waiting for a worker"
      />
      <Figure
        label="In flight"
        value={q ? formatNumber(q.in_flight) : undefined}
        note="Leased by a worker"
      />
      <Figure
        label="Scheduled"
        value={q ? formatNumber(q.scheduled) : undefined}
        note="Scheduled sends and retries"
      />
      <Figure
        label="Oldest waiting"
        value={q ? (age == null ? 'None' : formatDuration(age)) : undefined}
        note="Age of the oldest ready job"
        level={age == null ? undefined : age >= 600 ? 'critical' : age >= 60 ? 'warning' : 'good'}
      />
      <Figure
        label="Workers"
        value={info ? `${info.workers.alive} of ${info.workers.total}` : undefined}
        note={
          info && info.workers.total > info.workers.alive
            ? `${formatNumber(info.workers.total - info.workers.alive)} stale (stopped or restarted)`
            : 'Heartbeat in the last minute'
        }
        level={info ? (info.workers.alive === 0 ? 'critical' : 'good') : undefined}
      />
    </Card>
  )
}

const col = tableColumns<WorkerStatus>()
const workerColumns = col.columns([
  col.accessor('alive', {
    header: 'Status',
    cell: (c) =>
      c.getValue() ? (
        <span className="inline-flex items-center gap-1.5 text-success">
          <span className="size-2 rounded-full bg-success" aria-hidden /> Alive
        </span>
      ) : (
        // Usually a stopped or restarted process, not a fault: the alive count
        // and the "no workers" alert carry the real problem.
        <span className="inline-flex items-center gap-1.5 text-ink-2">
          <span className="size-2 rounded-full bg-line-strong" aria-hidden /> Stale
        </span>
      ),
    meta: { width: '7rem' },
  }),
  col.accessor('worker_id', {
    header: 'Worker',
    cell: (c) => (
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-ink">{c.getValue()}</div>
        <div className="truncate text-xs text-ink-3">{c.row.original.hostname}</div>
      </div>
    ),
    enableHiding: false,
  }),
  col.accessor('role', {
    header: 'Role',
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
    meta: { width: '6rem' },
  }),
  col.accessor('version', {
    header: 'Version',
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
    meta: { width: '7rem' },
  }),
  col.accessor('started_at', {
    header: 'Started',
    cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
    sortFn: 'datetime',
    meta: { width: '8rem' },
  }),
  col.accessor('last_seen_seconds', {
    header: 'Last seen',
    cell: (c) => (
      <span className={cn('tabular', c.row.original.alive ? 'text-ink-2' : 'text-ink-3')}>
        {formatDuration(c.getValue())} ago
      </span>
    ),
    meta: { align: 'right', width: '8rem' },
  }),
])

function Workers({
  info,
  isLoading,
}: {
  info: SystemInfo | undefined
  isLoading: boolean
}): ReactNode {
  const w = info?.workers
  return (
    <section aria-label="Workers" className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Workers</h2>
        {w ? (
          <span className="text-xs text-ink-2">
            Stale workers haven’t sent a heartbeat in over a minute. Their leased jobs return to the
            queue when the lease expires.
          </span>
        ) : (
          <Skeleton className="h-4 w-64" />
        )}
      </div>
      <DataTable
        aria-label="Workers"
        columns={workerColumns}
        data={w?.workers ?? []}
        getRowId={(x) => x.worker_id}
        isLoading={isLoading}
        initialSorting={[{ id: 'last_seen_seconds', desc: false }]}
        columnToggle={false}
        empty={
          <EmptyState
            compact
            icon={Cpu}
            title="No workers have reported"
            description="Start a process with MAILSMITH_ROLE=worker or all. Until one runs, accepted messages wait in the queue."
          />
        }
      />
    </section>
  )
}
